import * as Yup from "yup";

const UPPERCASE_REGEX = /[A-Z]/;
const LOWERCASE_REGEX = /[a-z]/;
const NUMBER_REGEX = /\d/;
const SPECIAL_CHAR_REGEX = /[!@#$%^&*?,.:;(_+=)-]/;

export const passwordValidationSchema = Yup.object().shape({
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must include at least 8 characters")
    .matches(UPPERCASE_REGEX, "Password must include an uppercase letter")
    .matches(LOWERCASE_REGEX, "Password must include a lowercase letter")
    .matches(NUMBER_REGEX, "Password must include a number")
    .matches(SPECIAL_CHAR_REGEX, "Password must include a special character (!@#$%^&*?,.:;()-_+=)"),
  confirmPassword: Yup.string()
    .required("Confirm Password is required")
    .oneOf([Yup.ref("password"), null], "Passwords don't match"),
});

// ─── Reusable RTP ─────────────────────────────────────────────────────────────

const DECIMAL_AMOUNT_REGEX = /^\d+(\.\d{1,2})?$/;
const CUSTOMER_NAME_MAX_LENGTH = 100;
const CUSTOMER_NAME_REGEX = /^(?=.*[a-zA-Z])[a-zA-Z\s\-']+$/;

/**
 * Validates the amount field for variable-amount reusable RTPs.
 * When isVariable is false, the field is skipped (fixed amount is read-only).
 */
const variableAmountSchema = Yup.string()
  .required("Please enter an amount")
  .matches(DECIMAL_AMOUNT_REGEX, "Please enter a valid amount (e.g. 10.00)")
  .test("is-positive", "Amount must be greater than 0", (value) => {
    if (!value) return false;
    return parseFloat(value) > 0;
  });

const customerNameRequiredSchema = Yup.string()
  .required("Please enter your name")
  .max(CUSTOMER_NAME_MAX_LENGTH, `Name must be ${CUSTOMER_NAME_MAX_LENGTH} characters or less`)
  .matches(CUSTOMER_NAME_REGEX, "Name must only contain letters")
  .test(
    "no-whitespace-only",
    "Please enter your name",
    (value) => !!value && value.trim().length > 0,
  );

const customerNameOptionalSchema = Yup.string()
  .max(CUSTOMER_NAME_MAX_LENGTH, `Name must be ${CUSTOMER_NAME_MAX_LENGTH} characters or less`)
  .matches(CUSTOMER_NAME_REGEX, {
    message: "Name must only contain letters",
    excludeEmptyString: true,
  })
  .test(
    "no-whitespace-only",
    "Name cannot contain only spaces",
    (value) => !value || value.trim().length > 0,
  );

export const tipAmountSchema = Yup.string()
  .matches(DECIMAL_AMOUNT_REGEX, {
    message: "Please enter a valid tip amount (e.g. 5.00)",
    excludeEmptyString: true,
  })
  .test("is-positive-tip", "Tip amount must be greater than 0", (value) => {
    if (!value) return true;
    return parseFloat(value) > 0;
  });

/**
 * Builds the form validation schema for the reusable RTP landing screen.
 * @param isVariableAmount - Whether the payer must enter their own amount.
 * @param requireCustomerName - Whether the customer name field is mandatory.
 * @param maxAmount - Optional upper bound for variable-amount RTPs.
 * @param allowTip - Whether the tip field should be validated.
 */
export const buildReusableRtpFormSchema = ({
  isVariableAmount,
  requireCustomerName,
  maxAmount,
  allowTip = false,
}: {
  isVariableAmount: boolean;
  requireCustomerName: boolean;
  maxAmount?: string;
  allowTip?: boolean;
}) => {
  const amountSchema = isVariableAmount
    ? variableAmountSchema.test(
        "max-amount",
        maxAmount ? `Amount must not exceed ${maxAmount}` : "Amount exceeds the maximum limit",
        (value) => !maxAmount || !value || parseFloat(value) <= parseFloat(maxAmount),
      )
    : Yup.string().notRequired();

  return Yup.object().shape({
    amount: amountSchema,
    customerName: requireCustomerName ? customerNameRequiredSchema : customerNameOptionalSchema,
    tip: allowTip ? tipAmountSchema : Yup.string().notRequired(),
  });
};
